'use client'

import { useState, useEffect, useMemo } from 'react'
import { apiFetch } from '@/lib/api'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { toast } from 'sonner'
import { Plus, Trash2, LogIn, LogOut, Coffee, Loader2, Pencil, ArrowRight, Briefcase, Tag } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { cn, formatTime } from '@/lib/utils'
import { PRIMARY_BTN } from '@/components/ui/button-styles'

interface BreakEntry { start: string | null; end: string | null }

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  userId: string
  date: string
  userName?: string
  currentCheckIn?: string | null
  currentCheckOut?: string | null
  currentBreaks?: BreakEntry[]
  currentWorkMode?: string | null
  currentStatus?: string | null
  onSaved: () => void
}

const WORK_MODES = ['OFFICE', 'WFH', 'FIELD']
const DAY_STATUSES = ['PRESENT', 'ABSENT', 'LEAVE', 'WFH', 'HOLIDAY', 'WEEKEND']

const HOURS = Array.from({ length: 12 }, (_, i) => String(i + 1))
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'))

function parse24(t?: string | null) {
  if (!t) return { hour: '9', minute: '00', period: 'AM' }

  let h: number, m: number
  if (/^\d{2}:\d{2}(:\d{2})?$/.test(t)) {
    const parts = t.split(':').map(Number)
    h = parts[0]; m = parts[1]
  } else {
    try {
      const d = new Date(t)
      if (isNaN(d.getTime())) return { hour: '9', minute: '00', period: 'AM' }
      h = d.getHours(); m = d.getMinutes()
    } catch {
      return { hour: '9', minute: '00', period: 'AM' }
    }
  }

  return {
    hour: String(h % 12 || 12),
    minute: String(m).padStart(2, '0'),
    period: h >= 12 ? 'PM' : 'AM',
  }
}

function to24(hour: string, minute: string, period: string) {
  let h = Number(hour) % 12
  if (period === 'PM') h += 12
  return `${String(h).padStart(2, '0')}:${minute}`
}

interface BrkUI { id: string; sH: string; sM: string; sP: string; eH: string; eM: string; eP: string; hasEnd: boolean }

// Stable per-row id so break rows key on identity, not array index — otherwise
// removing a row leaves the deleted row's controlled Select state on its neighbour.
let brkIdCounter = 0
const nextBrkId = () => `brk_${brkIdCounter++}`

function brkFromEntry(b: BreakEntry): BrkUI {
  const s = parse24(b.start)
  const e = parse24(b.end)
  return { id: nextBrkId(), sH: s.hour, sM: s.minute, sP: s.period, eH: e.hour, eM: e.minute, eP: e.period, hasEnd: !!b.end }
}

// hasEnd is derived from whether any of the end-time selects has a non-empty value.
// This stays in sync if the admin clears a field (prior bug: hasEnd was set true on first
// edit and never cleared).
function hasMeaningfulEnd(b: BrkUI): boolean {
  return !!(b.eH && b.eM && b.eP)
}

function brkToEntry(b: BrkUI): BreakEntry {
  const end = hasMeaningfulEnd(b) ? to24(b.eH, b.eM, b.eP) : null
  return {
    start: to24(b.sH, b.sM, b.sP),
    end,
  }
}

// Declared at module scope, not inside the component. Defining it inline made React
// treat it as a brand-new component type on every render, remounting all three
// Selects and dropping focus/open state mid-interaction.
function TimePicker({ h, m, p, onH, onM, onP, disabled, compact }: {
  h: string; m: string; p: string
  onH: (v: string) => void; onM: (v: string) => void; onP: (v: string) => void
  disabled?: boolean; compact?: boolean
}) {
  const trigger = cn(
    'bg-white dark:bg-white/5 border-neutral-200 dark:border-white/10 tabular-nums',
    compact ? 'h-8 w-[3.75rem] px-2 text-xs' : 'h-9 w-full text-sm',
    disabled && 'opacity-60'
  )
  return (
    <div className={cn('flex items-center', compact ? 'gap-1' : 'gap-1.5')}>
      <Select value={h} onValueChange={onH} disabled={disabled}>
        <SelectTrigger className={trigger}><SelectValue /></SelectTrigger>
        <SelectContent className="max-h-64">{HOURS.map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
      </Select>
      <span className={cn('text-neutral-400 dark:text-white/30', compact ? 'text-xs' : 'text-sm')}>:</span>
      <Select value={m} onValueChange={onM} disabled={disabled}>
        <SelectTrigger className={trigger}><SelectValue /></SelectTrigger>
        <SelectContent className="max-h-64">{MINUTES.map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={p} onValueChange={onP} disabled={disabled}>
        <SelectTrigger className={trigger}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="AM">AM</SelectItem>
          <SelectItem value="PM">PM</SelectItem>
        </SelectContent>
      </Select>
    </div>
  )
}

/** Section shell: an icon-led header with a toggle that arms the fields below. */
function EditSection({ icon: Icon, title, current, tone, enabled, onToggle, children }: {
  icon: LucideIcon
  title: string
  current: React.ReactNode
  tone: 'emerald' | 'blue' | 'amber' | 'violet'
  enabled: boolean
  onToggle: () => void
  children: React.ReactNode
}) {
  const TONE = {
    emerald: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-300 ring-emerald-500/20',
    blue: 'bg-blue-500/10 text-blue-600 dark:text-blue-300 ring-blue-500/20',
    amber: 'bg-amber-500/10 text-amber-600 dark:text-amber-300 ring-amber-500/20',
    violet: 'bg-violet-500/10 text-violet-600 dark:text-violet-300 ring-violet-500/20',
  }[tone]

  return (
    <div
      className={cn(
        'rounded-xl p-3 ring-1 ring-inset transition-colors',
        enabled
          ? 'bg-orange-50/60 ring-orange-500/30 dark:bg-orange-500/[0.06] dark:ring-orange-500/25'
          : 'bg-neutral-50 ring-neutral-200 dark:bg-white/[0.03] dark:ring-white/10'
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ring-1 ring-inset', TONE)}>
            <Icon className="h-3.5 w-3.5" />
          </span>
          <div className="min-w-0">
            <Label className="block text-[13px] font-medium text-neutral-900 dark:text-white/90">{title}</Label>
            <p className="truncate text-[11px] tabular-nums text-neutral-500 dark:text-white/50">{current}</p>
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          aria-label={`Edit ${title}`}
          onClick={onToggle}
          className={cn(
            'relative h-5 w-9 shrink-0 rounded-full ring-1 ring-inset transition-colors duration-200',
            enabled
              ? 'bg-orange-500 ring-orange-500'
              : 'bg-neutral-200 ring-neutral-300 dark:bg-white/10 dark:ring-white/15'
          )}
        >
          <span
            className={cn(
              // left-0.5 anchors the knob explicitly — with only top set, the
              // knob's static position inherits the button's UA text-align:center
              // and starts centered, pushing it past the track edge when on.
              'absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200 ease-out',
              enabled ? 'translate-x-4' : 'translate-x-0'
            )}
          />
        </button>
      </div>

      {/* Animate height 0↔content so expanding a section doesn't snap the dialog
          (and every button in it) to a new position — the modal is vertically
          centered, so an instant height change relocates the switch mid-click. */}
      <div
        className={cn(
          'grid transition-[grid-template-rows] duration-200 ease-out',
          enabled ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        )}
      >
        <div className="overflow-hidden">
          <div className="mt-3 border-t border-orange-500/15 pt-3">{children}</div>
        </div>
      </div>
    </div>
  )
}

export function AttendanceOverrideModal({ open, onOpenChange, userId, date, userName, currentCheckIn, currentCheckOut, currentBreaks, currentWorkMode, currentStatus, onSaved }: Props) {
  const [editIn, setEditIn] = useState(false)
  const [editOut, setEditOut] = useState(false)
  const [editBreaks, setEditBreaks] = useState(false)
  const [editWorkMode, setEditWorkMode] = useState(false)
  const [editStatus, setEditStatus] = useState(false)
  const [workMode, setWorkMode] = useState('OFFICE')
  const [status, setStatus] = useState('PRESENT')
  const [inH, setInH] = useState('9'); const [inM, setInM] = useState('00'); const [inP, setInP] = useState('AM')
  const [outH, setOutH] = useState('6'); const [outM, setOutM] = useState('00'); const [outP, setOutP] = useState('PM')
  const [brks, setBrks] = useState<BrkUI[]>([])
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const ci = parse24(currentCheckIn)
    setInH(ci.hour); setInM(ci.minute); setInP(ci.period)
    const co = parse24(currentCheckOut)
    setOutH(co.hour); setOutM(co.minute); setOutP(co.period)
    setBrks((currentBreaks || []).map(brkFromEntry))
    setWorkMode((currentWorkMode || 'OFFICE').toUpperCase())
    setStatus((currentStatus || 'PRESENT').toUpperCase())
    setEditIn(false); setEditOut(false); setEditBreaks(false); setEditWorkMode(false); setEditStatus(false)
    setReason('')
  }, [currentCheckIn, currentCheckOut, currentBreaks, currentWorkMode, currentStatus, open])

  const dirty = editIn || editOut || editBreaks || editWorkMode || editStatus

  const dateLabel = useMemo(() => {
    const d = new Date(date + 'T00:00:00')
    if (isNaN(d.getTime())) return date
    return d.toLocaleDateString('en-US', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' })
  }, [date])

  const save = async () => {
    if (!dirty) {
      toast.error('Turn on a field to edit first')
      return
    }
    setSaving(true)
    try {
      const body: any = { userId, date, reason }
      if (editIn) body.checkIn = to24(inH, inM, inP)
      if (editOut) body.checkOut = to24(outH, outM, outP)
      if (editBreaks) body.breaks = brks.map(brkToEntry)
      if (editWorkMode) body.workMode = workMode
      if (editStatus) body.status = status
      await apiFetch('/api/attendance/override', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      toast.success('Attendance updated')
      onSaved()
      onOpenChange(false)
    } catch (e: any) {
      console.error('Attendance override failed:', e)
      toast.error(e.message || 'Failed to save')
    } finally { setSaving(false) }
  }

  const addBreak = () => setBrks(prev => [...prev, { id: nextBrkId(), sH: '1', sM: '00', sP: 'PM', eH: '2', eM: '00', eP: 'PM', hasEnd: true }])
  const removeBreak = (i: number) => setBrks(prev => prev.filter((_, idx) => idx !== i))
  const updateBrk = (i: number, patch: Partial<BrkUI>) => setBrks(prev => prev.map((b, idx) => idx === i ? { ...b, ...patch } : b))

  const noneYet = <span className="text-neutral-400 dark:text-white/30">not recorded</span>

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] w-full max-w-lg flex-col gap-0 overflow-hidden border-neutral-200 bg-white/95 p-0 backdrop-blur-2xl dark:border-white/10 dark:bg-neutral-950/95">
        <DialogHeader className="shrink-0 space-y-0 border-b border-neutral-200 px-5 py-4 dark:border-white/10">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-orange-500/20 to-orange-500/5 ring-1 ring-inset ring-orange-500/20">
              <Pencil className="h-4 w-4 text-orange-600 dark:text-orange-300" />
            </span>
            <div className="min-w-0">
              <DialogTitle className="truncate text-base text-neutral-900 dark:text-white/90">
                {userName ? `Edit ${userName}'s attendance` : 'Edit attendance'}
              </DialogTitle>
              <DialogDescription className="text-xs text-neutral-500 dark:text-white/50">{dateLabel}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-4">
          <EditSection
            icon={LogIn}
            title="Check-in"
            tone="emerald"
            current={currentCheckIn ? formatTime(currentCheckIn) : noneYet}
            enabled={editIn}
            onToggle={() => setEditIn(v => !v)}
          >
            <TimePicker h={inH} m={inM} p={inP} onH={setInH} onM={setInM} onP={setInP} />
          </EditSection>

          <EditSection
            icon={LogOut}
            title="Check-out"
            tone="blue"
            current={currentCheckOut ? formatTime(currentCheckOut) : noneYet}
            enabled={editOut}
            onToggle={() => setEditOut(v => !v)}
          >
            <TimePicker h={outH} m={outM} p={outP} onH={setOutH} onM={setOutM} onP={setOutP} />
          </EditSection>

          <EditSection
            icon={Coffee}
            title="Breaks"
            tone="amber"
            current={
              (currentBreaks || []).length === 0
                ? <span className="text-neutral-400 dark:text-white/30">none recorded</span>
                : `${(currentBreaks || []).length} break${(currentBreaks || []).length > 1 ? 's' : ''}`
            }
            enabled={editBreaks}
            onToggle={() => setEditBreaks(v => !v)}
          >
            <div className="space-y-2">
              {brks.length === 0 && (
                <p className="rounded-lg border border-dashed border-neutral-200 py-3 text-center text-xs text-neutral-400 dark:border-white/10 dark:text-white/40">
                  No breaks — add one below
                </p>
              )}
              {brks.map((b, i) => (
                <div
                  key={b.id}
                  className="flex flex-wrap items-center gap-1.5 rounded-lg bg-white p-2 ring-1 ring-inset ring-neutral-200 dark:bg-white/[0.03] dark:ring-white/10"
                >
                  <span className="w-4 shrink-0 text-center text-[11px] font-medium tabular-nums text-neutral-400 dark:text-white/40">{i + 1}</span>
                  <TimePicker
                    compact
                    h={b.sH} m={b.sM} p={b.sP}
                    onH={(v) => updateBrk(i, { sH: v })}
                    onM={(v) => updateBrk(i, { sM: v })}
                    onP={(v) => updateBrk(i, { sP: v })}
                  />
                  <ArrowRight className="h-3 w-3 shrink-0 text-neutral-300 dark:text-white/25" />
                  <TimePicker
                    compact
                    h={b.eH} m={b.eM} p={b.eP}
                    onH={(v) => updateBrk(i, { eH: v, hasEnd: true })}
                    onM={(v) => updateBrk(i, { eM: v, hasEnd: true })}
                    onP={(v) => updateBrk(i, { eP: v, hasEnd: true })}
                  />
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label={`Remove break ${i + 1}`}
                    className="ml-auto h-7 w-7 shrink-0 hover:bg-rose-500/10"
                    onClick={() => removeBreak(i)}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-rose-500 dark:text-rose-400" />
                  </Button>
                </div>
              ))}
              <Button
                size="sm"
                variant="outline"
                onClick={addBreak}
                className="h-8 w-full border-dashed border-neutral-300 text-xs text-neutral-700 dark:border-white/15 dark:text-white/80"
              >
                <Plus className="mr-1 h-3 w-3" />Add break
              </Button>
            </div>
          </EditSection>

          <EditSection
            icon={Briefcase}
            title="Work Mode"
            tone="blue"
            current={(currentWorkMode || 'OFFICE').toUpperCase()}
            enabled={editWorkMode}
            onToggle={() => setEditWorkMode(v => !v)}
          >
            <Select value={workMode} onValueChange={setWorkMode}>
              <SelectTrigger className="w-full bg-white text-sm dark:bg-white/5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {WORK_MODES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </EditSection>

          <EditSection
            icon={Tag}
            title="Day Status"
            tone="violet"
            current={(currentStatus || 'PRESENT').toUpperCase()}
            enabled={editStatus}
            onToggle={() => setEditStatus(v => !v)}
          >
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-full bg-white text-sm dark:bg-white/5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DAY_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            {status === 'LEAVE' && (
              <p className="mt-2 text-[11px] text-amber-600 dark:text-amber-400">
                Marking a day as LEAVE here only changes the attendance record — file or edit the actual leave request under Leave Requests to keep balances in sync.
              </p>
            )}
          </EditSection>

          <div className="space-y-1.5 pt-1">
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-white/50">
              Reason <span className="font-normal normal-case tracking-normal text-neutral-400 dark:text-white/30">(optional, kept in the audit log)</span>
            </Label>
            <Textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              rows={2}
              placeholder="Timezone fix / forgot to check out / approved correction…"
              className="resize-none border-neutral-200 bg-white text-sm dark:border-white/10 dark:bg-white/5"
            />
          </div>
        </div>

        <DialogFooter className="shrink-0 items-center border-t border-neutral-200 bg-neutral-50/60 px-5 py-3 dark:border-white/10 dark:bg-white/[0.02] sm:justify-between">
          <p className="hidden text-[11px] text-neutral-400 dark:text-white/40 sm:block">
            {dirty ? 'Only the fields switched on will be changed.' : 'Switch on a field to start editing.'}
          </p>
          <div className="flex w-full gap-2 sm:w-auto">
            <Button variant="ghost" onClick={() => onOpenChange(false)} className="flex-1 text-neutral-700 dark:text-white/70 sm:flex-none">
              Cancel
            </Button>
            <Button onClick={save} disabled={saving || !dirty} className={cn(PRIMARY_BTN, 'flex-1 sm:flex-none')}>
              {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</> : 'Save changes'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
